import React, { useState, useEffect } from "react";
import {
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Box,
    Typography,
    Avatar,
    IconButton,
    useMediaQuery,
    Divider
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import LogoutIcon from "@mui/icons-material/ExitToApp";
import SegmentOutlinedIcon from '@mui/icons-material/SegmentOutlined';
import { useLocation, useNavigate } from "react-router-dom";
import Logo from "../../app/assets/logo-small.svg";
import styles from './styles.module.css';
import { axiosProxyInstance } from "../../utils/request";
import Cookies from 'js-cookie';

const waitForLocalStorageValue = (key, checkInterval = 100, timeout = 5000) => {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();

    const interval = setInterval(() => {
      const value = localStorage.getItem(key);

      if (value !== null) {
        clearInterval(interval);
        resolve(value);
      } else if (Date.now() - startTime > timeout) {
        clearInterval(interval);
        reject(new Error(`Timeout: localStorage key "${key}" not found within ${timeout}ms`));
      }
    }, checkInterval);
  });
};

const Sidebar = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [userName, setUserName] = useState();
    const [occupation, setOccupation] = useState();
    const [currentUserId, setCurrentUserId] = useState("");
    const isMobile = useMediaQuery("(max-width: 600px)");
    // const currentUserId = localStorage.getItem("LPCurrentUserId");

    const menuItems = [
        { text: "Your Listings", icon: <GridViewOutlinedIcon />, path: "/practitioner/listing" },
        { text: "Settings", icon: <SettingsIcon />, path: "/practitioner/settings" },
    ];

    const toggleDrawer = () => {
        setMobileOpen(!mobileOpen);
    };

    const fetchDetails = async () => {
        try {
            const response = await axiosProxyInstance.get(`/user/${currentUserId}`);
            const userData = response.data.data ?? {};
            setUserName(userData?.full_name);
            setOccupation(userData?.occupation);
            localStorage.setItem("LPCurrentUserName", userData?.full_name);
            localStorage.setItem("LPCurrentUserEmail", userData?.user_email);
        } catch (err) {
            console.log("Failed to fetch user");
        }
    };

    useEffect(() => {
        waitForLocalStorageValue("LPCurrentUserId")
        .then(userId => {
            setCurrentUserId(userId);
            // continue your logic here
        })
        .catch(err => {
            console.error(err.message);
            // handle the error or fallback
        });
    }, []);

     useEffect(() => {
        if (currentUserId) {
            fetchDetails();
        }
    }, [currentUserId]);

    const handleLogout = async () => {
        const payload = {
            uid: currentUserId,
            action: "logout"
        };
        try {
            const res = await axiosProxyInstance.post('/signout/', payload);
            if(res){
                localStorage.removeItem("LPCurrentUserId");
                localStorage.removeItem("currentCompleted");
                localStorage.removeItem("LPCurrentUserEmail");
                localStorage.removeItem("LPCurrentUserName");
                localStorage.removeItem("LPCurrentProvideruid");
                localStorage.removeItem("LPCurrentClinicId");
                localStorage.removeItem("LPCurrentClinicTitle");
                localStorage.removeItem("isOnboarding");
                Cookies.remove('wordpress_logged_in_user', { path: '/' });
                // navigate('/signup');
                const url = `${process.env.REACT_APP_WP_BACKEND_URL}/login/`;
                window.location.href = url;
            }
        } catch (err) {
            console.error("Submission failed:", err);
        }
        
    }

    const getInitials = (name) => {
        if (!name) return '';
        const words = name.trim().split(' ');
        return words.length === 1
          ? words[0][0]
          : words[0][0] + words[1][0];
      };

    const drawerContent = (
        <Box className={styles.sidebarContainer}>
            <Box className={styles.logoContainer}>
                <img src={Logo} alt="Logo" />
            </Box>
            <List>
                {menuItems.map((item) => (
                    <ListItem
                        button
                        key={item.text}
                        onClick={() => {
                            navigate(item.path);
                            if (isMobile) toggleDrawer();
                        }}
                        className={`${styles.menuItem} ${location.pathname === item.path ? styles.menuItemActive : ""}`}
                    >
                        <ListItemIcon className={styles.icon} style={{ color: location.pathname === item.path ? "#00AA9C" : "black" }}>
                            {item.icon}
                        </ListItemIcon>
                        <ListItemText primaryTypographyProps={{
                            fontWeight: "500",
                            fontSize: '14px'
                        }} primary={item.text} />
                    </ListItem>
                ))}
            </List>

            <Box className={styles.userInfo}>
                <Box className={styles.userInfoContainer}>
                    <Avatar className={styles.avatar}>{getInitials(userName).toUpperCase()}</Avatar>
                    <Box className={styles.userName}>
                        <Typography className={styles.menuItemText} variant="gothamRegular">{userName}</Typography>
                        <Typography variant="gothamRegular">{occupation}</Typography>
                    </Box>
                </Box>
                <Divider className={styles.userInfoContainer} />
                <ListItem button className={styles.logoutButton} onClick={() => handleLogout()}>
                    <ListItemIcon sx={{ minWidth: '35px !important' }}>
                        <LogoutIcon />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{
                        fontWeight: "500",
                        fontSize: '14px',
                    }} primary="Log Out" />
                </ListItem>
            </Box>
        </Box>
    );

    return (
        <>
            {isMobile && (
                <Box className={styles.topNavbar}>
                    <IconButton onClick={toggleDrawer}>
                        <SegmentOutlinedIcon className={styles.menuButton} />
                    </IconButton>
                    <img src={Logo} alt="Logo" />
                </Box>
            )}
            <Drawer
                variant={isMobile ? "temporary" : "permanent"}
                open={isMobile ? mobileOpen : true}
                onClose={toggleDrawer}
                sx={{
                    "& .MuiDrawer-paper": { width: 240, backgroundColor: "#E9F8F7", border: 'none' },
                }}
            >
                {drawerContent}
            </Drawer>
        </>
    );
};

export default Sidebar;
